import {
  AuthCommonDefaultProvider,
  Cached,
  LoginWithTokenFnReq,
  LoginWithTokenFnRes,
  ServiceAuthType,
  ServiceAuthTypeMap,
  ServiceProvider,
  ServiceProviderMap,
  TeamCommonDefaultProvider,
  User,
} from '@uploadcam/common';
import { Auth, User as AuthUser } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthDropboxProvider } from '../auth-dropbox/auth-dropbox.provider';
import { AuthGoogleProvider } from '../auth-google/auth-google.provider';
import { AuthMicrosoftProvider } from '../auth-microsoft/auth-microsoft.provider';
import { AuthZohoProvider } from '../auth-zoho/auth-zoho.provider';
import { CommonProvider } from '../common/common.provider';
import { GenerateAuthSecurityTokensInteractor } from './domain/interactors/generate-auth-security-tokens.interactor';
import { GetCredentialInteractor } from './domain/interactors/get-credential.interactor';
import { GetUserByIdInteractor } from './domain/interactors/get-user-by-id.interactor';
import { GetUserInteractor } from './domain/interactors/get-user.interactor';
import { IsLoggedInteractor } from './domain/interactors/is-logged.interactor';
import { LoginWithPopupInteractor } from './domain/interactors/login-with-popup.interactor';
import { LogoutInteractor } from './domain/interactors/logout.interactor';
import { RefreshCredentialInteractor } from './domain/interactors/refresh-credential.interactor';
import { GetAuthDtoInteractor } from './domain/interactors/get-auth-dto.interactor';
import {
  LoginWithRedirectAdapterInteractor,
  LoginWithRedirectInteractor,
} from './domain/interactors/login-with-redirect.interactor';
import { LoginFinishInteractor } from './domain/interactors/login-finish.interactor';
import { ExchangeAuthCodeInteractor } from './domain/interactors/exchange-auth-code.interactor';
import { LoginWithPopupAdapterInteractor } from './domain/interactors/login-with-popup-adapter.interactor';
import {
  CheckLoginPostConditionsInteractor,
  CheckLoginPostConditionsNoopInteractor,
} from './domain/interactors/check-login-post-conditions.interactor';
import { GetTeamByIdInteractor } from '../team/domain/interactors/get-team-by-id.interactor';
import { AnalyticsService } from '../../app/features/shared/services/analytics.service';
import { AuthenticateWithCredentialsInteractor } from './domain/interactors/authenticate-with-credentials.interactor';

export abstract class AuthProvider {
  abstract provideAuthenticateWithCredentials(): AuthenticateWithCredentialsInteractor;
  abstract provideGetAuthDto(): GetAuthDtoInteractor;
  abstract provideGetCredential(): GetCredentialInteractor;
  abstract provideGetUser(): GetUserInteractor;
  abstract provideGetUserById(): GetUserByIdInteractor;
  abstract provideIsLogged(): IsLoggedInteractor;
  abstract provideLoginFinish(): LoginFinishInteractor;
  abstract provideLoginWithPopup(): LoginWithPopupInteractor;
  abstract provideLoginWithRedirect(): LoginWithRedirectInteractor;
  abstract provideLogout(): LogoutInteractor;
  abstract provideRefreshCredential(): RefreshCredentialInteractor;
}

export class AuthDefaultProvider implements AuthProvider {
  private readonly authCommon = new AuthCommonDefaultProvider();
  private readonly teamCommon = new TeamCommonDefaultProvider();
  private readonly clientIds: ServiceProviderMap<string> = {
    [ServiceProvider.Dropbox]: environment.dropbox.clientId,
    [ServiceProvider.Google]: environment.google.clientId,
    [ServiceProvider.Microsoft]: environment.microsoft.clientId,
    [ServiceProvider.Zoho]: environment.zoho.clientId,
  };

  private readonly loginWithTokenFn = httpsCallable<LoginWithTokenFnReq, LoginWithTokenFnRes>(
    this.functions,
    'loginWithToken',
  );

  constructor(
    private readonly analytics: AnalyticsService,
    private readonly auth: Auth,
    private readonly authDropbox: AuthDropboxProvider,
    private readonly authGoogle: AuthGoogleProvider,
    private readonly authMicrosoft: AuthMicrosoftProvider,
    private readonly authZoho: AuthZohoProvider,
    private readonly common: CommonProvider,
    private readonly firestore: Firestore,
    private readonly functions: Functions,
    private readonly refreshCredentialError$: Subject<void>,
    private readonly userAuthStateChanged$: Subject<AuthUser | undefined>,
    private readonly userGetCurrent$: Subject<User>,
  ) {}

  @Cached()
  public provideAuthenticateWithCredentials(): AuthenticateWithCredentialsInteractor {
    const checkLoginPostConditionsNoop = new CheckLoginPostConditionsNoopInteractor();
    const checkLoginPostConditions: ServiceProviderMap<CheckLoginPostConditionsInteractor> = {
      [ServiceProvider.Dropbox]: checkLoginPostConditionsNoop,
      [ServiceProvider.Google]: checkLoginPostConditionsNoop,
      [ServiceProvider.Microsoft]: this.authMicrosoft.provideCheckLoginPostConditions(
        // TODO: This is a duplicate of `TeamDefaultProvider::provideGetTeamById`
        // There should be a better way of handling this. We take this shortcut
        // to avoid circular dependencies between AuthProvider and TeamProvider.
        new GetTeamByIdInteractor(this.firestore, this.teamCommon.provideTeamEntityToTeamMapper()),
        this.provideGetUser(),
      ),
      [ServiceProvider.Zoho]: checkLoginPostConditionsNoop,
    };

    return new AuthenticateWithCredentialsInteractor(
      this.auth,
      checkLoginPostConditions,
      this.clientIds,
      this.loginWithTokenFn,
      this.common.provideStorage(),
    );
  }

  @Cached()
  public provideGetAuthDto(): GetAuthDtoInteractor {
    return new GetAuthDtoInteractor(
      this.clientIds,
      this.provideGetCredential(),
      this.provideRefreshCredential(),
    );
  }

  @Cached()
  public provideGetCredential(): GetCredentialInteractor {
    return new GetCredentialInteractor(
      this.common.provideLogger().withTag('GetCredentialInteractor'),
      this.common.provideStorage(),
    );
  }

  @Cached()
  public provideGetUser(): GetUserInteractor {
    return new GetUserInteractor(
      this.auth,
      this.provideGetUserById(),
      this.userAuthStateChanged$,
      this.userGetCurrent$,
    );
  }

  @Cached()
  public provideGetUserById(): GetUserByIdInteractor {
    return new GetUserByIdInteractor(
      this.firestore,
      this.authCommon.provideUserEntityToUserMapper(),
    );
  }

  @Cached()
  public provideIsLogged(): IsLoggedInteractor {
    return new IsLoggedInteractor(
      this.auth,
      this.provideGetCredential(),
      this.userAuthStateChanged$,
    );
  }

  @Cached()
  public provideLoginFinish(): LoginFinishInteractor {
    const exchangeAuthCode: ServiceAuthTypeMap<ExchangeAuthCodeInteractor> = {
      [ServiceAuthType.Dropbox]: this.authDropbox.provideExchangeDropboxAuthCode(),
      [ServiceAuthType.Google]: this.authGoogle.provideExchangeGoogleAuthCode(),
      [ServiceAuthType.MicrosoftPersonalFull]:
        this.authMicrosoft.provideExchangeMicrosoftPersonalFullAuthCode(),
      [ServiceAuthType.MicrosoftTeamFull]:
        this.authMicrosoft.provideExchangeMicrosoftTeamFullAuthCode(),
      [ServiceAuthType.MicrosoftTeamSites]:
        this.authMicrosoft.provideExchangeMicrosoftTeamSitesAuthCode(),
      [ServiceAuthType.Zoho]: this.authZoho.provideExchangeZohoAuthCode(),
    };

    const requiredScopes: ServiceAuthTypeMap<readonly string[]> = {
      [ServiceAuthType.Dropbox]: this.authDropbox.provideRequiredScopes(),
      [ServiceAuthType.Google]: this.authGoogle.provideRequiredScopes(),
      [ServiceAuthType.MicrosoftPersonalFull]:
        this.authMicrosoft.provideRequiredScopesPersonalFull(),
      [ServiceAuthType.MicrosoftTeamFull]: this.authMicrosoft.provideRequiredScopesTeamFull(),
      [ServiceAuthType.MicrosoftTeamSites]: this.authMicrosoft.provideRequiredScopesTeamSites(),
      [ServiceAuthType.Zoho]: this.authZoho.provideRequiredScopes(),
    };

    return new LoginFinishInteractor(
      this.analytics,
      this.provideAuthenticateWithCredentials(),
      exchangeAuthCode,
      this.common.provideLogger().withTag('LoginFinishInteractor'),
      requiredScopes,
      this.common.provideStorage(),
    );
  }

  @Cached()
  public provideLoginWithPopup(): LoginWithPopupInteractor {
    const loginWithPopup: ServiceAuthTypeMap<LoginWithPopupAdapterInteractor> = {
      [ServiceAuthType.Dropbox]: this.authDropbox.provideLoginWithPopupDropboxAdapter(),
      [ServiceAuthType.Google]: this.authGoogle.provideLoginWithPopupGoogleAdapter(),
      [ServiceAuthType.MicrosoftPersonalFull]:
        this.authMicrosoft.provideLoginWithPopupMicrosoftPersonalFullAdapter(),
      [ServiceAuthType.MicrosoftTeamFull]:
        this.authMicrosoft.provideLoginWithPopupMicrosoftTeamFullAdapter(),
      [ServiceAuthType.MicrosoftTeamSites]:
        this.authMicrosoft.provideLoginWithPopupMicrosoftTeamSitesAdapter(),
      [ServiceAuthType.Zoho]: this.authZoho.provideLoginWithPopupZohoAdapter(),
    };

    return new LoginWithPopupInteractor(
      new GenerateAuthSecurityTokensInteractor(),
      this.common.provideLogger().withTag('LoginWithPopupInteractor'),
      this.provideLoginFinish(),
      loginWithPopup,
    );
  }

  @Cached()
  public provideLoginWithRedirect(): LoginWithRedirectInteractor {
    const loginWithRedirectAdapter: ServiceAuthTypeMap<LoginWithRedirectAdapterInteractor> = {
      [ServiceAuthType.Dropbox]: this.authDropbox.provideLoginWithRedirectDropboxAdapter(),
      [ServiceAuthType.Google]: this.authGoogle.provideLoginWithRedirectGoogleAdapter(),
      [ServiceAuthType.MicrosoftPersonalFull]:
        this.authMicrosoft.provideLoginWithRedirectMicrosoftPersonalFullAdapter(),
      [ServiceAuthType.MicrosoftTeamFull]:
        this.authMicrosoft.provideLoginWithRedirectMicrosoftTeamFullAdapter(),
      [ServiceAuthType.MicrosoftTeamSites]:
        this.authMicrosoft.provideLoginWithRedirectMicrosoftTeamSitesAdapter(),
      [ServiceAuthType.Zoho]: this.authZoho.provideLoginWithRedirectZohoAdapter(),
    };

    return new LoginWithRedirectInteractor(
      new GenerateAuthSecurityTokensInteractor(),
      this.common.provideLogger().withTag('LoginWithRedirectInteractor'),
      loginWithRedirectAdapter,
      this.common.provideStorage(),
    );
  }

  @Cached()
  public provideLogout(): LogoutInteractor {
    return new LogoutInteractor(this.auth, this.common.provideStorage());
  }

  @Cached()
  public provideRefreshCredential(): RefreshCredentialInteractor {
    return new RefreshCredentialInteractor(
      this.common.provideLogger().withTag('RefreshCredentialInteractor'),
      this.refreshCredentialError$,
      {
        [ServiceProvider.Dropbox]: this.authDropbox.provideRefreshDropboxCredential(),
        [ServiceProvider.Google]: this.authGoogle.provideRefreshGoogleCredential(),
        [ServiceProvider.Microsoft]: this.authMicrosoft.provideRefreshMicrosoftCredential(),
        [ServiceProvider.Zoho]: this.authZoho.provideRefreshZohoCredential(),
      },
      this.common.provideStorage(),
    );
  }
}
